import React, { useState, useEffect, useRef } from 'react';
import { clickable, useDrawerFocus } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const PRIORITIES = [
  { value: 'critical', label: '🔴 Critical', color: '#dc2626' },
  { value: 'high',     label: '🟠 High',     color: '#d97706' },
  { value: 'medium',   label: '🟡 Medium',   color: '#f59e0b' },
  { value: 'low',      label: '🟢 Low',      color: '#16a34a' },
];

const TEAM = [
  { name: 'Dimitri DE BACKER',  initials: 'DD', color: '#2563eb' },
  { name: 'Mohamed ATAUIL',      initials: 'MA', color: '#7c3aed' },
  { name: 'Aurélien BARBIEUX',   initials: 'AB', color: '#0891b2' },
  { name: 'Leslie BASHORUN',     initials: 'LB', color: '#d97706' },
  { name: 'Jonathan DEMORTIER',  initials: 'JD', color: '#dc2626' },
  { name: 'Kris MUNDORFF',        initials: 'KM', color: '#be185d' },
  { name: 'Pierre BUIDIN',        initials: 'PB', color: '#9333ea' },
];

const COLUMNS = [
  { id: 'todo',       label: '📋 To Do',      color: '#6366f1' },
  { id: 'inprogress', label: '⚙️ In Progress', color: '#0891b2' },
  { id: 'waiting',    label: '⏳ Waiting',     color: '#d97706' },
];

const ME = 'Dimitri DE BACKER';

function getMember(name) {
  return TEAM.find(m => m.name === name);
}

function priColor(p) {
  return PRIORITIES.find(x => x.value === p)?.color || '#f59e0b';
}


function getColTodos(todos, colId) {
  return todos.filter(t => {
    const isDone = t.done || t.status === 'done';
    if (isDone) return false;
    if (colId === 'todo') return !t.status || t.status === 'todo';
    return t.status === colId;
  }).sort((a, b) => {
    const po = { critical: 0, high: 1, medium: 2, low: 3 };
    return (po[a.priority] ?? 2) - (po[b.priority] ?? 2);
  });
}


// ── Smart Notes Editor: text + inline images ──
function SmartNotesEditor({ notes, onChange, onPaste }) {
  // Parse notes into segments: text blocks and image blocks
  const parseSegments = (text) => {
    const parts = text.split(/(!\[[^\]]*\]\([^)]+\)|\[📎 [^\]]+\]\([^)]+\))/g);
    return parts.map((part, i) => {
      const imgMatch = part.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imgMatch) return { type: 'image', alt: imgMatch[1], src: imgMatch[2], index: i };
      const fileMatch = part.match(/^\[📎 ([^\]]+)\]\(([^)]+)\)$/);
      if (fileMatch) return { type: 'file', name: fileMatch[1], src: fileMatch[2], index: i };
      return { type: 'text', value: part, index: i };
    });
  };

  const segments = parseSegments(notes || '');

  const updateSegment = (index, newValue) => {
    const newSegments = segments.map(s =>
      s.index === index ? { ...s, value: newValue } : s
    );
    onChange(newSegments.map(s =>
      s.type === 'image' ? `![${s.alt}](${s.src})` : s.value
    ).join(''));
  };

  const removeImage = (index) => {
    const newSegments = segments.filter(s => s.index !== index);
    onChange(newSegments.map(s =>
      s.type === 'image' ? `![${s.alt}](${s.src})` : s.value
    ).join(''));
  };

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', gap:4, minHeight:200 }}>
      {segments.length === 0 || (segments.length === 1 && segments[0].type === 'text' && !segments[0].value) ? (
        <textarea
          value={notes || ''}
          onChange={e => onChange(e.target.value)}
          onPaste={onPaste}
          placeholder="Add notes… paste a screenshot with Ctrl+V"
          style={{ flex:1, fontFamily:'DM Sans', fontSize:13, border:'1px solid var(--border)', borderRadius:8,
            padding:'10px 12px', resize:'none', outline:'none', background:'var(--surface-2)', lineHeight:1.7,
            minHeight:200, color:'var(--text)' }}
        />
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {segments.map(seg => {
            if (seg.type === 'image') {
              return (
                <div key={seg.index} style={{ position:'relative', display:'inline-block' }}>
                  <img src={seg.src} alt={seg.alt}
                    style={{ maxWidth:'100%', borderRadius:8, border:'1px solid var(--border)', display:'block' }} />
                  <button onClick={() => removeImage(seg.index)}
                    title="Remove image"
                    style={{ position:'absolute', top:6, right:6, background:'rgba(0,0,0,.6)', border:'none',
                      color:'#fff', borderRadius:4, cursor:'pointer', fontSize:12, padding:'2px 7px', lineHeight:1 }}>
                    ✕
                  </button>
                  {seg.alt && seg.alt !== 'pasted image' && (
                    <div style={{ fontSize:10, color:'var(--text-2)', marginTop:2 }}>{seg.alt}</div>
                  )}
                </div>
              );
            }
            if (seg.type === 'file') {
              return (
                <div key={seg.index} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:7 }}>
                  <span style={{ fontSize:16 }}>📎</span>
                  <span style={{ flex:1, fontSize:12, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{seg.name}</span>
                  <a href={seg.src} download={seg.name}
                    style={{ fontSize:11, padding:'3px 10px', borderRadius:5, background:'var(--accent)', color:'#fff', textDecoration:'none', fontWeight:600, flexShrink:0 }}>
                    Download
                  </a>
                  <button onClick={() => removeImage(seg.index)} title="Remove"
                    style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-2)', fontSize:14, lineHeight:1, padding:'0 2px' }}>×</button>
                </div>
              );
            }
            return (
              <textarea
                key={seg.index}
                value={seg.value}
                onChange={e => updateSegment(seg.index, e.target.value)}
                onPaste={onPaste}
                placeholder={seg.index === 0 ? "Add notes… paste a screenshot with Ctrl+V" : "Add text…"}
                rows={Math.max(2, (seg.value.match(/\n/g)||[]).length + 2)}
                style={{ fontFamily:'DM Sans', fontSize:13, border:'1px solid var(--border)', borderRadius:8,
                  padding:'8px 12px', resize:'none', outline:'none', background:'var(--surface-2)',
                  lineHeight:1.7, color:'var(--text)', width:'100%', boxSizing:'border-box' }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Detail Drawer ──
function TodoDrawer({ todo, onClose, onUpdate, onRemove, moveToColumn, customers, onNewCustomer }) {
  const drawerRef = useRef(null);
  useDrawerFocus(drawerRef);
  const [text, setText] = useState(todo.text);
  const [notes, setNotes] = useState(todo.notes || '');
  const [customer, setCustomer] = useState(todo.customer || '');
  const [saved, setSaved] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();
  const isDone = todo.done || todo.status === 'done';
  const pri = PRIORITIES.find(p => p.value === todo.priority) || PRIORITIES[2];

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const save = () => {
    const c = customer.trim();
    onUpdate(todo.id, { text: text.trim(), notes, customer: c });
    if (c && onNewCustomer) onNewCustomer(c);
    onClose();
  };

  const attachFile = (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (file.type.startsWith('image/')) {
        setNotes(n => n + `\n![${file.name}](${ev.target.result})`);
      } else {
        setNotes(n => (n ? n + '\n' : '') + `[📎 ${file.name}](${ev.target.result})`);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    attachFile(file);
    e.target.value = '';
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        const reader = new FileReader();
        reader.onload = (ev) => {
          const img = `\n![pasted image](${ev.target.result})`;
          setNotes(n => n + img);
        };
        reader.readAsDataURL(file);
        e.preventDefault();
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      files.forEach(attachFile);
    } else {
      const dropped = e.dataTransfer.getData('text/plain');
      if (dropped) setNotes(n => (n ? n + '\n\n' : '') + dropped);
    }
  };

  // Render notes with images, linkifying URLs in text segments
  const linkify = (text, keyPrefix) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) =>
      /^https?:\/\//.test(part)
        ? <a key={keyPrefix + '-' + i} href={part} target="_blank" rel="noopener noreferrer"
            style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>{part}</a>
        : part
    );
  };

  const renderNotes = (text) => {
    const parts = text.split(/(!\[[^\]]*\]\([^)]+\)|\[📎 [^\]]+\]\([^)]+\))/g);
    return parts.map((part, i) => {
      const imgMatch = part.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imgMatch) return (
        <div key={i} style={{ margin: '8px 0' }}>
          <img src={imgMatch[2]} alt={imgMatch[1]} style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid var(--border)' }} />
          {imgMatch[1] && <div style={{ fontSize: 10, color: 'var(--text-2)', marginTop: 2 }}>{imgMatch[1]}</div>}
        </div>
      );
      const fileMatch = part.match(/^\[📎 ([^\]]+)\]\(([^)]+)\)$/);
      if (fileMatch) return (
        <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', background:'var(--surface-2)', border:'1px solid var(--border)', borderRadius:7, margin:'4px 0' }}>
          <span style={{ fontSize:16 }}>📎</span>
          <span style={{ flex:1, fontSize:12, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{fileMatch[1]}</span>
          <a href={fileMatch[2]} download={fileMatch[1]}
            style={{ fontSize:11, padding:'3px 10px', borderRadius:5, background:'var(--accent)', color:'#fff', textDecoration:'none', fontWeight:600, flexShrink:0 }}>
            Download
          </a>
        </div>
      );
      if (!part) return null;
      return <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{linkify(part, i)}</span>;
    });
  };

  return (
    <>
      <div onClick={onClose} className="drawer-overlay" />
      <div className="drawer" style={{ width: 560, maxWidth: '95vw' }}
        role="dialog" aria-modal="true" aria-label="Edit to-do" tabIndex={-1} ref={drawerRef}>

        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
            <div style={{ flex:1 }}>
              <textarea value={text} onChange={e => setText(e.target.value)}
                style={{ width:'100%', fontFamily:'DM Sans', fontSize:15, fontWeight:600, border:'none', outline:'none', background:'transparent', resize:'none', lineHeight:1.4, color:'var(--text)', boxSizing:'border-box' }}
                rows={text.length > 60 ? 2 : 1} />
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-2)', flexShrink:0, fontSize:20, lineHeight:1 }}>×</button>
          </div>

          {/* Meta row */}
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginTop:8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <span style={{ fontSize:11, color:'var(--text-2)', fontWeight:600 }}>👤 Customer:</span>
              <input list="customer-list-drawer" value={customer} onChange={e => setCustomer(e.target.value)}
                placeholder="Add customer…"
                style={{ fontFamily:'DM Sans', fontSize:12, padding:'3px 8px', border:'1px solid var(--border)', borderRadius:5, background:'var(--surface-2)', outline:'none', width:180 }} />
              <datalist id="customer-list-drawer">
                {(customers||[]).map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <select value={todo.assignee||''} onChange={e => onUpdate(todo.id,{assignee:e.target.value})}
              style={{ fontFamily:'DM Sans', fontSize:11, padding:'3px 6px', border:'1px solid var(--border)', borderRadius:5, background:'var(--surface-2)', color:'var(--text-2)', cursor:'pointer', outline:'none' }}>
              <option value="">👤 Unassigned</option>
              {TEAM.map(m => <option key={m.name} value={m.name}>{m.initials} — {m.name.split(' ')[0]} {m.name.split(' ').slice(-1)[0]}</option>)}
            </select>
            <div style={{ display:'flex', gap:4 }}>
              {COLUMNS.map(c => {
                const cur = isDone ? 'done' : (todo.status||'todo');
                if (c.id === cur) return <span key={c.id} style={{ fontSize:10, padding:'3px 8px', borderRadius:4, background:c.color+'22', color:c.color, fontWeight:700 }}>{c.label}</span>;
                return <button key={c.id} onClick={() => { moveToColumn(todo.id,c.id); onClose(); }}
                  style={{ fontSize:10, padding:'3px 8px', borderRadius:4, border:`1px solid ${c.color}44`, background:'transparent', color:c.color, cursor:'pointer', fontFamily:'DM Sans' }}>
                  → {c.label.split(' ').slice(1).join(' ')}
                </button>;
              })}
            </div>
            <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
              <button onClick={() => { onUpdate(todo.id,{done:!isDone,status:isDone?'todo':'done'}); }}
                style={{ fontSize:11, padding:'3px 10px', borderRadius:5, border:`1px solid ${isDone?'#16a34a':'var(--border)'}`, background:isDone?'#16a34a22':'transparent', color:isDone?'#16a34a':'var(--text-2)', cursor:'pointer', fontFamily:'DM Sans' }}>
                {isDone ? '✓ Done' : '○ Mark done'}
              </button>
              <button onClick={() => { onRemove(todo.id); onClose(); }}
                style={{ fontSize:11, padding:'3px 10px', borderRadius:5, border:'1px solid var(--critical)', background:'transparent', color:'var(--critical)', cursor:'pointer', fontFamily:'DM Sans' }}>
                Delete
              </button>
            </div>
          </div>
          {todo.created && <div style={{ fontSize:10, color:'var(--text-2)', marginTop:6 }}>Created {new Date(todo.created).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}</div>}
        </div>

        {/* Notes area */}
        <div className="drawer-body" style={{ padding:'16px 20px', display:'flex', flexDirection:'column', gap:10, position:'relative' }}
          onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOver(true); }}
          onDragEnter={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
          onDrop={handleDrop}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontSize:12, fontWeight:600 }}>📝 Notes & Screenshots</div>
            <div style={{ display:'flex', gap:6 }}>
              <button onClick={() => fileRef.current.click()}
                style={{ fontSize:11, padding:'3px 10px', borderRadius:5, border:'1px solid var(--border)', background:'var(--surface-2)', cursor:'pointer', fontFamily:'DM Sans' }}>
                📎 Attach
              </button>
            </div>
            <input ref={fileRef} type="file" accept="*/*" style={{ display:'none' }} onChange={handleFile} />
          </div>

          {/* Drop overlay */}
          {dragOver && (
            <div style={{ position:'absolute', inset:0, zIndex:10, background:'var(--accent-light)', border:'2px dashed var(--accent)',
              borderRadius:8, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, pointerEvents:'none' }}>
              <svg width="32" height="32" fill="none" stroke="var(--accent)" strokeWidth="1.5" viewBox="0 0 24 24">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--accent)' }}>Drop to attach</div>
              <div style={{ fontSize:11, color:'var(--accent)', opacity:.7 }}>Images embed inline · other files added as reference</div>
            </div>
          )}

          {/* Smart editor: text + inline images */}
          <SmartNotesEditor notes={notes} onChange={setNotes} onPaste={handlePaste} />
        </div>

        {/* Save button */}
        <div className="drawer-footer" style={{ justifyContent:'flex-end' }}>
          {saved && <span style={{ fontSize:12, color:'var(--ok)', alignSelf:'center' }}>✓ Saved</span>}
          <button onClick={save} className="btn btn-primary" style={{ padding:'7px 24px' }}>Save</button>
        </div>
      </div>
    </>
  );
}

// ── Compact Todo Card ──
function TodoCard({ todo, onToggle, onRemove, onUpdate, moveToColumn, onOpen }) {
  const isDone = todo.done || todo.status === 'done';
  const hasNotes = todo.notes && todo.notes.trim().length > 0;
  const member = getMember(todo.assignee);
  const [dragging, setDragging] = useState(false);

  return (
    <div
      draggable={!isDone}
      onDragStart={e => {
        e.dataTransfer.setData('todoId', String(todo.id));
        e.dataTransfer.effectAllowed = 'move';
        setDragging(true);
      }}
      onDragEnd={() => setDragging(false)}
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
        padding: '5px 8px', marginBottom: 4, borderLeft: `3px solid ${priColor(todo.priority)}`,
        opacity: dragging ? 0.4 : isDone ? 0.5 : 1,
        display:'flex', alignItems:'center', gap:6,
        cursor: isDone ? 'default' : 'grab',
        transition: 'opacity .15s',
      }}>
      {/* Done toggle */}
      <button onClick={() => onToggle(todo.id, todo.status)}
        style={{ background:'none', border:`1px solid ${isDone?'#16a34a':'var(--border)'}`, borderRadius:3, cursor:'pointer', padding:'1px 4px', fontSize:10, color:isDone?'#16a34a':'var(--text-2)', flexShrink:0, lineHeight:1 }}>
        {isDone ? '✓' : '○'}
      </button>

      {/* Title */}
      <div {...clickable(() => onOpen(todo))} aria-label={todo.text} style={{ flex:1, fontSize:12, lineHeight:1.3, cursor:'pointer',
        textDecoration: isDone ? 'line-through' : 'none', color: isDone ? 'var(--text-2)' : 'var(--text)',
        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
        title={todo.text}>
        {todo.customer && <span style={{ fontSize:9, background:'var(--accent-light)', color:'var(--accent)', borderRadius:3, padding:'1px 4px', marginRight:4, fontWeight:600 }}>{todo.customer}</span>}
        {todo.text}
      </div>

      {/* Indicators */}
      <div style={{ display:'flex', alignItems:'center', gap:3, flexShrink:0 }}>
        {hasNotes && <span title="Has notes" style={{ fontSize:9, color:'var(--accent)' }}>📝</span>}
        {member && (
          <div title={member.name} style={{ width:16,height:16,borderRadius:'50%',background:member.color,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:7,fontWeight:700 }}>
            {member.initials}
          </div>
        )}
        <button onClick={() => onOpen(todo)} title="Open"
          style={{ background:'none', border:'1px solid var(--border)', borderRadius:3, cursor:'pointer', padding:'1px 5px', fontSize:9, color:'var(--text-2)', lineHeight:1 }}>
          ↗
        </button>
        <button onClick={() => onRemove(todo.id)}
          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-2)', fontSize:13, lineHeight:1, padding:'0 2px' }}>×</button>
      </div>
    </div>
  );
}

// ── Person Board ──
function PersonBoard({ name, todos, onToggle, onRemove, onUpdate, moveToColumn, onOpen, defaultCollapsed, customers }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed || false);
  const [showDone, setShowDone]   = useState(false);
  const [dragOverCol, setDragOverCol] = useState(null);
  const member = getMember(name) || { name, initials: name.slice(0,2).toUpperCase(), color: '#94a3b8' };
  const active   = todos.filter(t => !t.done && t.status !== 'done').length;
  const doneTodos = todos
    .filter(t => t.done || t.status === 'done')
    .sort((a, b) => (b.id || 0) - (a.id || 0));   // most recently added first

  if (active === 0 && doneTodos.length === 0) return null;

  return (
    <div style={{ marginBottom:12, border:'1px solid var(--border)', borderRadius:10, overflow:'hidden' }}>
      {/* Person header */}
      <div {...clickable(() => setCollapsed(c => !c))} aria-expanded={!collapsed} aria-label={`${name}'s to-dos`}
        style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 14px', background:'var(--surface-2)', cursor:'pointer', borderBottom: collapsed?'none':'1px solid var(--border)' }}>
        <div style={{ width:28,height:28,borderRadius:'50%',background:member.color,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0 }}>
          {member.initials}
        </div>
        <div style={{ flex:1 }}>
          <span style={{ fontWeight:700, fontSize:13 }}>{name}</span>
          <span style={{ fontSize:11, color:'var(--text-2)', marginLeft:8 }}>{active} active</span>
          {doneTodos.length > 0 && (
            <span style={{ fontSize:11, color:'#16a34a', marginLeft:6 }}>· {doneTodos.length} done</span>
          )}
        </div>
        <span style={{ fontSize:11, color:'var(--text-2)' }}>{collapsed ? '▶' : '▼'}</span>
      </div>

      {!collapsed && (
        <>
          {/* 3-column active board */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)' }}>
            {COLUMNS.map((col, ci) => {
              const colTodos  = getColTodos(todos, col.id);
              const isOver    = dragOverCol === col.id;
              return (
                <div key={col.id}
                  onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverCol(col.id); }}
                  onDragEnter={e => { e.preventDefault(); setDragOverCol(col.id); }}
                  onDragLeave={e => {
                    // only clear if leaving the column entirely (not just entering a child)
                    if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null);
                  }}
                  onDrop={e => {
                    e.preventDefault();
                    const id = parseInt(e.dataTransfer.getData('todoId'));
                    if (id) moveToColumn(id, col.id);
                    setDragOverCol(null);
                  }}
                  style={{
                    borderRight: ci<2?'1px solid var(--border)':'none', minHeight:60,
                    background: isOver ? col.color + '12' : 'transparent',
                    outline: isOver ? `2px dashed ${col.color}88` : 'none',
                    outlineOffset: -2,
                    transition: 'background .1s',
                  }}>
                  <div style={{ padding:'5px 8px', borderBottom:'1px solid var(--border)', background: isOver ? col.color+'18' : 'var(--surface)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ fontSize:10, fontWeight:700, color:col.color }}>{col.label}</span>
                    {colTodos.length > 0 && <span style={{ fontSize:9, background:col.color+'22', color:col.color, borderRadius:99, padding:'1px 5px', fontWeight:600 }}>{colTodos.length}</span>}
                    {isOver && <span style={{ fontSize:9, color:col.color, fontWeight:600 }}>Drop here ↓</span>}
                  </div>
                  <div style={{ padding:'6px 6px' }}>
                    {colTodos.map(todo => (
                      <TodoCard key={todo.id} todo={todo} onToggle={onToggle} onRemove={onRemove} onUpdate={onUpdate} moveToColumn={moveToColumn} onOpen={onOpen} customers={customers} />
                    ))}
                    {colTodos.length === 0 && (
                      <div style={{ fontSize:10, color: isOver ? col.color : 'var(--text-2)', textAlign:'center', padding:'10px 0', opacity: isOver ? .8 : .3, fontWeight: isOver ? 600 : 400 }}>
                        {isOver ? '+ Drop here' : '—'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Collapsible Done section */}
          {doneTodos.length > 0 && (
            <div style={{ borderTop:'1px solid var(--border)' }}>
              <button onClick={() => setShowDone(d => !d)}
                style={{ width:'100%', padding:'6px 14px', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:8, fontSize:11, color:'var(--text-2)', fontFamily:'DM Sans', textAlign:'left' }}>
                <span style={{ color:'#16a34a' }}>✅</span>
                <span style={{ fontWeight:600 }}>Done</span>
                <span style={{ background:'#16a34a22', color:'#16a34a', borderRadius:99, padding:'1px 7px', fontFamily:'DM Mono', fontSize:10, fontWeight:700 }}>{doneTodos.length}</span>
                <span style={{ marginLeft:'auto', fontSize:10 }}>{showDone ? '▲ hide' : '▼ show'}</span>
              </button>
              {showDone && (
                <div style={{ padding:'6px 10px 8px', borderTop:'1px solid var(--border)', background:'var(--surface-2)' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0 8px' }}>
                    {doneTodos.map(todo => (
                      <TodoCard key={todo.id} todo={todo} onToggle={onToggle} onRemove={onRemove} onUpdate={onUpdate} moveToColumn={moveToColumn} onOpen={onOpen} customers={customers} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main Page ──
export default function TodoPage() {
  const [todos, setTodos]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput]     = useState('');
  const [priority, setPriority] = useState('medium');
  const [assignee, setAssignee] = useState(ME);
  const [search, setSearch]   = useState('');
  const [openTodo, setOpenTodo] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [newCustomer, setNewCustomer] = useState('');
  const inputRef = useRef();

  useEffect(() => {
    fetch(`${API}/api/todos/customers`)
      .then(r => r.json())
      .then(d => setCustomers(d.customers || []))
      .catch(() => {});
    fetch(`${API}/api/todos`)
      .then(r => r.json())
      .then(d => { setTodos(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const add = async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    try {
      const r = await fetch(`${API}/api/todos`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ text, assignee, status:'todo', customer: newCustomer || '' }),
      });
      const todo = await r.json();
      setTodos(t => [todo, ...t]);
      if (todo.customer) setCustomers(c => [...new Set([...c, todo.customer])]);
      setNewCustomer('');
    } catch { setInput(text); }
    inputRef.current?.focus();
  };

  const toggle = async (id, currentStatus) => {
    const cur = todos.find(t => t.id===id);
    const isDone = cur?.done || cur?.status === 'done';
    const newStatus = isDone ? 'todo' : 'done';
    setTodos(t => t.map(x => x.id===id ? {...x, status:newStatus, done:!isDone} : x));
    await fetch(`${API}/api/todos/${id}`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({done:!isDone,status:newStatus}) });
  };

  const remove = async (id) => {
    setTodos(t => t.filter(x => x.id!==id));
    await fetch(`${API}/api/todos/${id}`, { method:'DELETE' });
  };

  const update = async (id, changes) => {
    setTodos(t => t.map(x => x.id===id ? {...x,...changes} : x));
    if (openTodo?.id === id) setOpenTodo(t => ({...t,...changes}));
    await fetch(`${API}/api/todos/${id}`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify(changes) });
  };

  const moveToColumn = async (id, colId) => {
    const isDone = colId==='done';
    setTodos(t => t.map(x => x.id===id ? {...x, status:colId, done:isDone} : x));
    await fetch(`${API}/api/todos/${id}`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({status:colId,done:isDone}) });
  };

  const filtered = search ? todos.filter(t => t.text.toLowerCase().includes(search.toLowerCase())) : todos;
  const myTodos = filtered.filter(t => t.assignee === ME);
  // Only current team members get a board. Someone who has left keeps their
  // finished to-dos in the database, but no longer takes up a row here.
  const otherAssignees = [...new Set(filtered.filter(t => t.assignee && t.assignee !== ME).map(t => t.assignee))]
    .filter(name => TEAM.some(m => m.name === name))
    .sort();
  const unassigned = filtered.filter(t => !t.assignee || t.assignee === '');
  const totalActive = todos.filter(t => !t.done && t.status !== 'done').length;
  const totalDone   = todos.filter(t =>  t.done || t.status === 'done').length;

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 56px)', overflow:'hidden' }}>
      {/* Toolbar */}
      <div style={{ padding:'8px 16px', borderBottom:'1px solid var(--border)', background:'var(--surface)', flexShrink:0 }}>
        <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:6 }}>
          <input list="customer-list-toolbar" placeholder="👤 Customer (optional)"
            value={newCustomer}
            onChange={e => setNewCustomer(e.target.value)}
            style={{ fontFamily:'DM Sans', fontSize:11, padding:'5px 6px', border:'1px solid var(--border)', borderRadius:6, background:'var(--surface-2)', outline:'none', width:160 }} />
          <datalist id="customer-list-toolbar">
            {customers.map(c => <option key={c} value={c} />)}
          </datalist>
          <select value={assignee} onChange={e => setAssignee(e.target.value)}
            style={{ fontFamily:'DM Sans', fontSize:11, padding:'5px 6px', border:'1px solid var(--border)', borderRadius:6, background:'var(--surface-2)', cursor:'pointer', outline:'none' }}>
            <option value="">👤 Unassigned</option>
            {TEAM.map(m => <option key={m.name} value={m.name}>{m.initials} — {m.name.split(' ')[0]} {m.name.split(' ').slice(-1)[0]}</option>)}
          </select>
          <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key==='Enter' && add()} placeholder="Add a task… (press Enter)"
            style={{ flex:1, fontFamily:'DM Sans', fontSize:13, padding:'5px 10px', border:'1px solid var(--border)', borderRadius:6, outline:'none', background:'var(--surface-2)' }} />
          <button onClick={add} className="btn btn-primary" style={{ flexShrink:0, padding:'5px 16px', fontSize:12 }}>Add</button>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search…"
            style={{ flex:1, fontFamily:'DM Sans', fontSize:11, padding:'4px 8px', border:'1px solid var(--border)', borderRadius:5, outline:'none', background:'var(--surface-2)' }} />
          <span style={{ fontSize:11, color:'var(--text-2)', whiteSpace:'nowrap' }}>{totalActive} active · {totalDone} done</span>
        </div>
      </div>

      {/* Column header strip */}
      <div style={{ display:'grid', gridTemplateColumns:'200px repeat(3,1fr)', background:'var(--surface-2)', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
        <div style={{ padding:'4px 14px', fontSize:10, fontWeight:600, color:'var(--text-2)', display:'flex', alignItems:'center' }}>ASSIGNEE</div>
        {COLUMNS.map(col => (
          <div key={col.id} style={{ padding:'5px 8px', textAlign:'center', fontSize:10, fontWeight:700, color:col.color, borderLeft:'1px solid var(--border)' }}>
            {col.label}
          </div>
        ))}
      </div>

      {/* Boards */}
      <div style={{ flex:1, overflowY:'auto', padding:'10px 12px' }}>
        {loading && <div className="loading">Loading tasks…</div>}
        {!loading && (
          <>
            <PersonBoard name={ME} todos={myTodos} onToggle={toggle} onRemove={remove} onUpdate={update} moveToColumn={moveToColumn} onOpen={setOpenTodo} defaultCollapsed={false} customers={customers} />
            {(otherAssignees.length > 0 || unassigned.length > 0) && (
              <div style={{ display:'flex', alignItems:'center', gap:10, margin:'6px 0 10px' }}>
                <div style={{ flex:1, height:1, background:'var(--border)' }} />
                <span style={{ fontSize:10, color:'var(--text-2)', fontWeight:600, letterSpacing:'.5px' }}>OTHER TEAM MEMBERS</span>
                <div style={{ flex:1, height:1, background:'var(--border)' }} />
              </div>
            )}
            {otherAssignees.map(name => (
              <PersonBoard key={name} name={name} todos={filtered.filter(t => t.assignee===name)}
                onToggle={toggle} onRemove={remove} onUpdate={update} moveToColumn={moveToColumn} onOpen={setOpenTodo} defaultCollapsed={false} customers={customers} />
            ))}
            {unassigned.length > 0 && (
              <PersonBoard name="Unassigned" todos={unassigned}
                onToggle={toggle} onRemove={remove} onUpdate={update} moveToColumn={moveToColumn} onOpen={setOpenTodo} defaultCollapsed={true} customers={customers} />
            )}
          </>
        )}
      </div>

      {/* Detail drawer */}
      {openTodo && (
        <TodoDrawer
          todo={openTodo}
          onClose={() => setOpenTodo(null)}
          onUpdate={update}
          onRemove={remove}
          moveToColumn={moveToColumn}
          customers={customers}
          onNewCustomer={c => setCustomers(prev => [...new Set([...prev, c])])}
        />
      )}
    </div>
  );
}
